import { Box } from '@chakra-ui/react';
import React from 'react';

import type { PaneSpec } from '../../config';
import { useCodingConfig } from '../../interface';
import { CaseNotesActivityPanel } from '../../molecules/CaseNotesActivityPanel';
import { ResizableSplit } from '../../molecules/ResizableSplit';
import { DocumentationPane } from '../../organisms/DocumentationPane';
import { HeaderBar } from '../../organisms/HeaderBar';
import { PageFooter } from '../../organisms/PageFooter';
import { ReviewTransactionsPane } from '../../organisms/ReviewTransactionsPane';
import {
  PAGE_BG,
  SHELL_PAD,
  WORKSPACE_PAD,
  WORKSPACE_REGION_BG
} from '../../styles/tokens';

/** Maps a pane's fixed `id` to the organism it renders -- `'left'` is always `DocumentationPane`,
 *  `'right'` is always `ReviewTransactionsPane`. Which VISUAL side of the split (its `left`/`right`
 *  slot) each id lands in is a separate question, driven by `order`, not `id` -- see
 *  `CodingScreenTemplate` below. */
function paneOrganism(pane: PaneSpec): React.ReactNode {
  return pane.id === 'left' ? (
    <DocumentationPane />
  ) : (
    <ReviewTransactionsPane />
  );
}

/**
 * The page skeleton: three stacked rows -- HeaderBar (fixed), a `ResizableSplit`-driven content row
 * (fills remaining space), PageFooter (fixed) -- each wrapped in its own labelled `role="region"`
 * landmark. Never renders a `<main>` (CMx already provides one); the root is a plain `Box`.
 *
 * `data-mode={config.mode}` on the root lets a later Shell/mode test assert which bench (`coding`
 * vs `grading`) this instance renders for -- the empty shell renders identically either way; only
 * the attribute differs.
 *
 * Which organism lands in which `ResizableSplit` slot is driven entirely by `config.panes`, never
 * hard-coded: panes are sorted by `order`, and the sizing props (`leftMinPx`/`rightMinPx`/
 * `initialLeftPct`) come from that SAME sorted pair -- so a future config that swaps which id has
 * `order: 0` moves both the organism and its sizing to the other visual side together, never one
 * without the other.
 */
export const CodingScreenTemplate: React.FC = () => {
  const config = useCodingConfig();
  // `validateConfig` (config/schema.ts) enforces "exactly one left + one right pane" upstream, so
  // this isn't reachable via any of this file's own default/allowed configs today -- but
  // `CodingScreenProvider` performs no validation itself (interface/context.tsx), so nothing
  // actually stops an unvalidated or future-buggy config from reaching this render. Guard the
  // destructure explicitly rather than let it fail deep inside JSX (`paneOrganism`'s `pane.id`,
  // `secondaryPane.minPx`, ...) with a cryptic "Cannot read properties of undefined" that doesn't
  // name what's actually wrong.
  if (config.panes.length !== 2) {
    throw new Error(
      `CodingScreenTemplate requires exactly 2 panes (one 'left' + one 'right'); received ${config.panes.length}. This should have been caught by validateConfig() before this template was ever rendered.`
    );
  }
  const [primaryPane, secondaryPane] = [...config.panes].sort(
    (a, b) => a.order - b.order
  );

  return (
    <Box
      data-mode={config.mode}
      display="flex"
      flexDirection="column"
      flex="1"
      minH={0}
      h="100%"
      bg={PAGE_BG}
      p={SHELL_PAD}
      padding={0}
    >
      <Box role="region" aria-label="Header" flexShrink={0} borderBottom={"1px solid #d6d4d9"}>
        <HeaderBar />
      </Box>
      <Box
        role="region"
        aria-label="Documentation and review"
        flex="1"
        minH={0}
        padding={WORKSPACE_PAD}
        bg={WORKSPACE_REGION_BG}
      >
        <ResizableSplit
          left={paneOrganism(primaryPane)}
          right={paneOrganism(secondaryPane)}
          leftMinPx={primaryPane.minPx}
          rightMinPx={secondaryPane.minPx}
          initialLeftPct={primaryPane.sizePct}
          ariaLabel="Coding workspace panes"
        />
      </Box>
      <Box role="region" aria-label="Footer" flexShrink={0}>
        <PageFooter />
      </Box>
      <CaseNotesActivityPanel />
    </Box>
  );
};
